import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../../services/apiClient';
import { toastService } from '../../services/toastService';
import { usePermissions } from '../../hooks/usePermissions';
import PageHeader from '../common/PageHeader/PageHeader';
import Button from '../common/Button/Button';
import MCPServerModal from './MCPServerModal';
import './MCPServers.css';

// Backend uses Literal["ok", "auth_failed", "unreachable", "tls_error"]
// for last_health_status. null/undefined when never checked yet.
const STATUS_COLOR = {
  ok: 'mcp-pill-green',
  auth_failed: 'mcp-pill-amber',
  unreachable: 'mcp-pill-red',
  tls_error: 'mcp-pill-red',
};

function StatusPill({ status }) {
  const cls = STATUS_COLOR[status] || 'mcp-pill-gray';
  return <span className={`mcp-pill ${cls}`}>{status || 'unknown'}</span>;
}

export default function MCPServers() {
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission('mcp', 'write');
  const canDelete = hasPermission('mcp', 'delete');
  const [servers, setServers] = useState(null); // null = loading
  const [expanded, setExpanded] = useState({}); // serverId → { tools, error, loading }
  const [busyRow, setBusyRow] = useState(null); // serverId currently being tested
  const [modal, setModal] = useState(null); // { mode: 'create' | 'edit', server: ?obj }

  const refresh = useCallback(async () => {
    try {
      const list = await apiClient.listMcpServers();
      setServers(list);
    } catch (e) {
      // toast already fired by apiClient.handleError
      setServers([]);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const retryTools = async (server) => {
    setExpanded((prev) => ({ ...prev, [server.id]: { tools: null, error: null, loading: true } }));
    try {
      const body = await apiClient.listMcpServerTools(server.id);
      setExpanded((prev) => ({ ...prev, [server.id]: { tools: body.tools, error: body.error, loading: false } }));
    } catch (e) {
      setExpanded((prev) => ({ ...prev, [server.id]: { tools: [], error: e.message, loading: false } }));
    }
  };

  const toggleExpand = async (server) => {
    const id = server.id;
    if (expanded[id]) {
      setExpanded((prev) => { const n = { ...prev }; delete n[id]; return n; });
      return;
    }
    setExpanded((prev) => ({ ...prev, [id]: { tools: null, error: null, loading: true } }));
    try {
      const body = await apiClient.listMcpServerTools(id);
      setExpanded((prev) => ({ ...prev, [id]: { tools: body.tools, error: body.error, loading: false } }));
    } catch (e) {
      setExpanded((prev) => ({ ...prev, [id]: { tools: [], error: e.message, loading: false } }));
    }
  };

  const handleTest = async (server) => {
    setBusyRow(server.id);
    try {
      await apiClient.testMcpServer(server.id);
      await refresh();
      toastService.success(`Tested ${server.name}`);
    } catch (e) {
      // toast already fired
    } finally {
      setBusyRow(null);
    }
  };

  if (servers === null) {
    return (
      <div className="mcp-servers-page">
        <PageHeader title="MCP Servers" description="Configure tool integrations via the Model Context Protocol." />
        <table className="mcp-table">
          <tbody>
            {[0, 1, 2].map((i) => (
              <tr key={i} className="mcp-skeleton-row"><td colSpan={8}>&nbsp;</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="mcp-servers-page">
      <PageHeader
        title="MCP Servers"
        description="Configure tool integrations via the Model Context Protocol."
        actions={
          <Button
            variant="primary"
            disabled={!canWrite}
            title={canWrite ? '' : 'Requires mcp:write'}
            onClick={() => setModal({ mode: 'create', server: null })}
          >
            Add MCP Server
          </Button>
        }
      />

      {servers.length === 0 ? (
        <div className="mcp-empty">
          <p>No MCP servers yet.</p>
          <p className="mcp-empty-hint">Connect an MCP server to expose its tools to the supervisor.</p>
        </div>
      ) : (
        <table className="mcp-table">
          <thead>
            <tr>
              <th style={{ width: 32 }} />
              <th>Name</th>
              <th>URL</th>
              <th>Transport</th>
              <th>Auth</th>
              <th>Enabled</th>
              <th>Status</th>
              <th style={{ width: 200 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {servers.map((server) => {
              const exp = expanded[server.id];
              const isBusy = busyRow === server.id;
              return (
                <React.Fragment key={server.id}>
                  <tr>
                    <td>
                      <button
                        className="mcp-chevron"
                        onClick={() => toggleExpand(server)}
                        aria-label={exp ? 'Collapse' : 'Expand'}
                      >
                        <i className={`fas fa-chevron-${exp ? 'down' : 'right'}`} />
                      </button>
                    </td>
                    <td>{server.name}</td>
                    <td><code>{server.server_url}</code></td>
                    <td>{server.transport}</td>
                    <td>
                      {server.auth_type}{' '}
                      <span className="mcp-auth-status">({server.auth_status})</span>
                    </td>
                    <td>{server.enabled ? 'Yes' : 'No'}</td>
                    <td><StatusPill status={server.last_health_status} /></td>
                    <td className="mcp-actions">
                      <Button size="sm" disabled={!canWrite || isBusy} isLoading={isBusy} onClick={() => handleTest(server)}>
                        Test
                      </Button>
                      <Button size="sm" disabled={!canWrite} onClick={() => setModal({ mode: 'edit', server })}>Edit</Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={!canDelete}
                        onClick={async () => {
                          if (!window.confirm(`Delete ${server.name}? This cannot be undone.`)) return;
                          try {
                            await apiClient.deleteMcpServer(server.id);
                            toastService.success(`Deleted ${server.name}`);
                            await refresh();
                          } catch (e) { /* toast already fired */ }
                        }}
                      >
                        Delete
                      </Button>
                      {server.auth_type === 'oauth2' && server.auth_status !== 'connected' && (
                        <Button
                          size="sm"
                          disabled={!canWrite || isBusy}
                          onClick={async () => {
                            setBusyRow(server.id);
                            try {
                              sessionStorage.setItem('mcpOAuthFlow', JSON.stringify({
                                serverId: server.id,
                                serverName: server.name,
                                expiresAt: Date.now() + 30 * 60 * 1000,
                              }));
                              const { authorize_url } = await apiClient.startMcpOAuth(server.id);
                              window.location.href = authorize_url;
                            } catch (e) {
                              sessionStorage.removeItem('mcpOAuthFlow');
                              setBusyRow(null);
                            }
                          }}
                        >
                          Connect
                        </Button>
                      )}
                      {server.auth_type === 'oauth2' && server.auth_status === 'connected' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!canWrite}
                          onClick={async () => {
                            if (!window.confirm(`Revoke OAuth tokens for ${server.name}?`)) return;
                            try {
                              await apiClient.revokeMcpOAuth(server.id);
                              toastService.success(`Revoked ${server.name}`);
                              await refresh();
                            } catch (e) { /* toast already fired */ }
                          }}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                  {exp && (
                    <tr className="mcp-expand-row">
                      <td colSpan={8}>
                        {exp.loading && <p className="mcp-tools-loading">Loading tools…</p>}
                        {!exp.loading && exp.error && (
                          <div className="mcp-tools-error">
                            Couldn&apos;t fetch tools: {exp.error}{' '}
                            <Button size="sm" variant="secondary" onClick={() => retryTools(server)}>Retry</Button>
                          </div>
                        )}
                        {!exp.loading && !exp.error && (exp.tools?.length ? (
                          <ul className="mcp-tools-list">
                            {exp.tools.map((t) => (
                              <li key={t.name}>
                                <code>{t.name}</code> — {t.description || <em>(no description)</em>}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mcp-tools-empty">No tools available.</p>
                        ))}
                        {server.last_health_status === 'auth_failed' && server.auth_type === 'oauth2' && (
                          <div className="mcp-tools-error">
                            Authentication failed. Click <strong>Connect</strong> to re-authorize.
                          </div>
                        )}
                        <div className="mcp-health-footer">
                          {server.last_health_check_at && (
                            <span>Last checked: {new Date(server.last_health_check_at).toLocaleString()}</span>
                          )}
                          {server.last_health_error && (
                            <span className="mcp-health-error">Error: {server.last_health_error}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
      {modal && (
        <MCPServerModal
          mode={modal.mode}
          server={modal.server}
          onClose={() => setModal(null)}
          onSaved={(updated) => {
            setExpanded((prev) => {
              if (!updated || !prev[updated.id]) return prev;
              const n = { ...prev };
              delete n[updated.id];
              return n;
            });
            refresh();
          }}
        />
      )}
    </div>
  );
}
